import { useContext, useEffect, useState } from "react";
import { ProfileContext } from "../contexts/AppContext";
import { AnimatePresence } from "motion/react";
import * as motion from "motion/react-client";
import { challenges } from "../contexts/ChallengeData";

import "./screens.scss";
import "./game.scss";

import { gameBgTransition, gameModalTransition, noTransition } from "../transitions";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

export default function Game() {
  const { setProfile, carColor, happiness, time, battery, timeBonus, currentChallenge } = useContext(ProfileContext);
  if (!setProfile) return null;

  const navigate = useNavigate();

  const activeChallenge = currentChallenge || 0;
  const [nextChallenge, setNextChallenge] = useState(0);
  const [showQuestion, setShowQuestion] = useState(true);
  const [showFact, setShowFact] = useState(false);
  const [bgTransition, setBgTransition] = useState(gameBgTransition);

  const {
    register,
    reset,
    handleSubmit,
    formState: { isValid },
  } = useForm<{ choice: number }>({ defaultValues: { choice: undefined } });

  const onSubmit = (data) => {
    
    const choice = challenges[activeChallenge].question.options[data.choice];
    const newTime = time + choice.time;
    const newHappiness = happiness + choice.happiness;
    const newBattery = choice.battery ? choice.battery : battery;
    const newTimeBonus = timeBonus + bonusTime;

    const nextId = activeChallenge + 1;
    setNextChallenge(nextId);

    // set up the next transition
    if (nextId < challenges.length && challenges[nextId].imageUrl === challenges[activeChallenge].imageUrl) {
      setBgTransition(noTransition);
    } else {
      setBgTransition(gameBgTransition);
    }

    // update the profile with the new values (scores)
    setProfile({
      happiness: newHappiness,
      time: newTime,
      battery: newBattery,
      timeBonus: newTimeBonus,
    });

    // Hide the question
    setShowQuestion(false);

    // Show the fact or complete the challenge
    const fact = challenges[activeChallenge].fact;
    if (fact) {
      setShowFact(true);
    } else {
      setShouldComplete(true);
    }
  };

  // Once the challenge is completed, we need to wait 2 seconds before moving on to the next challenge
  const [shouldComplete, setShouldComplete] = useState(false);
  useEffect(() => {
    let completeTimer;

    if (shouldComplete) {
      completeTimer = setTimeout(() => {
        completeChallenge();
        setShouldComplete(false);
      }, 2000);
    }

    return () => clearTimeout(completeTimer);
  }, [shouldComplete]);

  const completeChallenge = () => {
    // Continue to next challenge or finish at the concert
    if (nextChallenge >= challenges.length) {
      navigate("/concert");
    } else {
      setProfile({
        currentChallenge: nextChallenge,
      });

      setShowQuestion(true);
    }
    reset();
    // Reset the bonus time
    setBonusTime(duration);
  };

  useEffect(() => {
    // Set battery override if it exists in the challenge
    if (challenges[activeChallenge]?.battery) {
      setProfile({
        battery: challenges[activeChallenge].battery,
      });
    }
  }, [activeChallenge]);

  // default bonus time for the challenge is 20 seconds
  const duration = 10000;
  const [bonusTime, setBonusTime] = useState(duration);

  useEffect(() => {
    const interval = setInterval(() => {
      setBonusTime((prev) => Math.max(0, prev - 500));
    }, 500);

    return () => clearInterval(interval);
  }, [activeChallenge]);

  return (
    <section className="screen screen--game">
      <AnimatePresence mode={"popLayout"} initial>
        {challenges.map(
          (challenge, index) =>
            activeChallenge === index && (
              <motion.section
                key={"challenge-" + index}
                className="challenge"
                style={{ backgroundImage: `url(${challenge.imageUrl})` }}
                {...bgTransition}
              >
                {challenge.carType.length > 0 && (
                  <div
                    className="car"
                    style={{ backgroundImage: `url("${challenge.carType + carColor?.toLowerCase()}.png")` }}
                  />
                )}
                <AnimatePresence mode={"wait"}>
                  {showQuestion && (
                    <motion.section
                      key={`challenge-${index}`}
                      className="challenge__modal challenge__modal--question"
                      {...gameModalTransition}
                    >
                      <header className="challenge__header">
                        <h1>Challenge #{index + 1}</h1>
                        <p>{challenge.question.text}</p>
                      </header>
                      <section className="challenge__options">
                        <form id="challenge" className="challenge__form" onSubmit={handleSubmit(onSubmit)}>
                          {challenge.question.options.map((option, id) => (
                            <label key={`label-${index}${id}`} className="challenge__label">
                              <div className="challenge__button-wrapper">
                                <input
                                  key={`input-${index}${id}`}
                                  {...register("choice", { required: true })}
                                  type="radio"
                                  value={id}
                                  className="challenge__button"
                                />
                                <span>{option.text}</span>
                              </div>
                            </label>
                          ))}
                        </form>
                      </section>
                      <input
                        type="submit"
                        value="Submit"
                        form="challenge"
                        className="button button--black"
                        disabled={!isValid}
                      />
                    </motion.section>
                  )}
                  {showFact && (
                    <motion.section
                      key={`fact-${index}`}
                      className="challenge__modal challenge__modal--fact"
                      {...gameModalTransition}
                    >
                      <header className="challenge__header challenge__header--fact">
                        <h1>Did you know?</h1>
                      </header>
                      <section className="challenge__fact">
                        <p>{challenge.fact}</p>
                      </section>
                      <button
                        className="button button--black"
                        onClick={() => {
                          setShouldComplete(true);
                          setShowFact(false);
                        }}
                      >
                        Next
                      </button>
                    </motion.section>
                  )}
                </AnimatePresence>
              </motion.section>
            )
        )}
      </AnimatePresence>
    </section>
  );
}
